import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

const responseCache = new Map(); // 요청 key → hotple_id list
const hotpleCache = new Map();   // hotple_id → hotple 객체
const MAX_HOTPLE_COUNT = 2000;

const cleanUpHotpleCacheByCount = () => {
  const total = hotpleCache.size;

  if (total <= MAX_HOTPLE_COUNT) return;

  // 정렬: 최신이 먼저, 오래된 게 뒤에
  const sorted = [...hotpleCache.entries()].sort(
    (a, b) => b[1].timestamp - a[1].timestamp
  );

  // 앞 500개(최신), 뒤 500개(오래된) 제외하고 중간 1000개 제거
  const middleItems = sorted.slice(500, sorted.length - 500);

  for (const [hotpleId] of middleItems) {
    hotpleCache.delete(hotpleId);

    // 해당 hotpleId가 포함된 responseCache들도 제거
    for (const [key, hotpleIds] of responseCache.entries()) {
      if (hotpleIds.includes(hotpleId)) {
        responseCache.delete(key);
      }
    }
  }
};

export const updateHotpleCache = (hotple) => {
  const id = hotple?.hotple_id;
  if (!id || !hotpleCache.has(id)) {
    return false;
  }
  
  hotpleCache.set(id, {
    data: hotple,
    timestamp: Date.now(),
  });

  return true;
};

const generateCacheKey = (config) => {
  const { method, url, params, data, headers } = config;
  const keyData = method === 'get' ? params : data;

  return `${method.toUpperCase()} ${url}?${JSON.stringify(keyData || {})} ${headers}`;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // 캐시 사용 가능할 때만 요청 중단
  const cacheKey = generateCacheKey(config);

  const isCacheTarget =
    config.method === 'get' &&
    (config.url === '/hotple/range' || config.url === '/hotple/region');

  if (isCacheTarget && responseCache.has(cacheKey)) {
    const hotpleIds = responseCache.get(cacheKey);
    const hotples = hotpleIds
      .map(id => hotpleCache.get(id).data)
      .filter(Boolean); // 혹시 빠진 게 있다면 제외

    config.adapter = () => {
      return Promise.resolve({
        data: {
          ...config.cachedFallback || {}, // 필요 시 다른 response 구조 병합
          hotples,
        },
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
        request: {},
      });
    };
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const config = response.config;
    const cacheKey = generateCacheKey(config);
    const isCacheTarget =
      config.method === 'get' &&
      (config.url === '/hotple/range' || config.url === '/hotple/region');

    if (isCacheTarget) {
      const hotples = response.data.hotples || [];
      const hotpleIds = [];

      for (const hotple of hotples) {
        const id = hotple.hotple_id;
        hotpleIds.push(id);

        hotpleCache.set(id, {
          data: hotple,
          timestamp: Date.now(),
        });
      }

      responseCache.set(cacheKey, hotpleIds);

      cleanUpHotpleCacheByCount(); // ✅ 여기서 캐시 정리
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken && !originalRequest._retry) {
        originalRequest._retry = true; // 이 플래그를 통해 무한 루프 방지
        try {
          const response = await api.post('/auth/refresh_token', {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // 실패한 요청을 새로운 토큰으로 다시 시도
          return api(originalRequest);
        } catch (refreshError) {
          // 리프레시 토큰 만료 또는 리프레시 실패 시 토큰 제거 및 403 처리
          if (refreshError.response && refreshError.response.status === 403) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.reload(); // 403 에러 시 새로고침
          }
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// 카카오 키워드로 장소 검색하기 API 호출 함수
export const callSearchingPlaces = async (keyword) => {
  try {
    const response = await axios.get('https://dapi.kakao.com/v2/local/search/keyword.json', {
      headers: {
        Authorization: `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`, // .env 파일에 KAKAO API KEY 설정 필요
      },
      params: {
        query: keyword,
      },
    });

    return response.data.documents;
  } catch (error) {
    console.error('Error searching places:', error);
    return [];
  }
};

export const clearHotpleCache = () => {
  responseCache.clear();
};

export default api;